import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Navbar, Footer } from '../components';
import api from '../utils/api';

const Support = () => {
  const { isAuthenticated } = useSelector((state) => state.auth);
  const navigate = useNavigate();

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState(null);

  // Modal State
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    subject: '',
    category: 'general',
    priority: 'low',
    message: '',
    attachment: null
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
      return;
    }
    fetchTickets(1);
  }, [isAuthenticated, navigate]);

  const fetchTickets = async (page = 1) => {
    setLoading(true);
    try {
      const res = await api.get(`/api/tickets?page=${page}`);
      setTickets(res.data.data);
      setMeta({
        current_page: res.data.current_page,
        last_page: res.data.last_page,
      });
    } catch (err) {
      toast.error('Gagal memuat daftar tiket');
    } finally {
      setLoading(false);
    }
  };

  const statusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-amber-500/15 text-amber-500 ring-amber-500/30';
      case 'answered': return 'bg-emerald-500/15 text-emerald-400 ring-emerald-500/30';
      case 'closed': return 'bg-zinc-500/15 text-zinc-400 ring-zinc-500/30';
      default: return 'bg-zinc-500/15 text-zinc-400 ring-zinc-500/30';
    }
  };

  const priorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-rose-500';
      case 'medium': return 'text-amber-500';
      case 'low': return 'text-emerald-500';
      default: return 'text-zinc-500';
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFormData({ ...formData, attachment: e.target.files[0] });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.subject || !formData.message) {
      return toast.error("Judul dan deskripsi harus diisi");
    }

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('subject', formData.subject);
      fd.append('category', formData.category);
      fd.append('priority', formData.priority);
      fd.append('message', formData.message);
      if (formData.attachment) {
        fd.append('attachment', formData.attachment);
      }

      await api.post('/api/tickets', fd, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success("Tiket berhasil dibuat");
      setShowModal(false);
      setFormData({ subject: '', category: 'general', priority: 'low', message: '', attachment: null });
      fetchTickets(1);
    } catch (err) {
      toast.error(err.response?.data?.message || "Gagal membuat tiket");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isAuthenticated) return null;

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-zinc-950 text-white pb-20 pt-10 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          
          <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-extrabold tracking-tight">Pusat Bantuan</h1>
              <p className="mt-2 text-sm text-zinc-400">Kelola tiket dukungan Anda dan pantau status tanggapan dari tim kami.</p>
            </div>
            <button
              onClick={() => setShowModal(true)}
              className="inline-flex items-center justify-center rounded-xl bg-white px-5 py-2.5 text-sm font-bold text-black shadow-sm transition hover:bg-zinc-200"
            >
              + Buat Tiket Baru
            </button>
          </div>

          <div className="rounded-3xl border border-white/10 bg-zinc-900/50 p-6 md:p-8">
            {loading ? (
              <div className="flex justify-center py-20">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-zinc-800 border-t-emerald-500"></div>
              </div>
            ) : tickets.length === 0 ? (
              <div className="text-center py-16">
                <svg className="mx-auto h-16 w-16 text-zinc-600 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
                <h3 className="text-xl font-bold">Tidak ada tiket</h3>
                <p className="text-zinc-400 mt-2">Anda belum memiliki riwayat tiket bantuan.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {tickets.map(ticket => (
                  <Link
                    key={ticket.id}
                    to={`/support/ticket/${ticket.id}`}
                    className="block rounded-2xl border border-white/5 bg-white/5 hover:bg-white/10 transition p-5"
                  >
                    <div className="flex flex-col sm:flex-row justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <span className={`inline-flex items-center mt-0.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset ${statusColor(ticket.status)}`}>
                            {ticket.status.toUpperCase()}
                          </span>
                          <span className="text-xs text-zinc-500">
                            #{ticket.id.toString().padStart(5, '0')}
                          </span>
                        </div>
                        <h4 className="text-lg font-bold text-white mb-1 group-hover:text-emerald-400 transition">{ticket.subject}</h4>
                        <div className="flex items-center gap-4 text-xs font-medium text-zinc-400">
                          <span className="uppercase">{ticket.category.replace('_', ' ')}</span>
                          <span className="flex items-center gap-1">
                            Prioritas: <span className={priorityColor(ticket.priority)}>{ticket.priority}</span>
                          </span>
                          <span>{new Date(ticket.created_at).toLocaleDateString('id-ID')}</span>
                        </div>
                      </div>
                      
                      <div className="sm:text-right flex flex-row sm:flex-col items-center sm:items-end justify-between sm:justify-center">
                        <span className="text-sm font-semibold text-zinc-300">
                          {ticket.replies_count} balasan
                        </span>
                        <div className="text-emerald-500 hidden sm:block mt-2">
                           <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                           </svg>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}

                {/* Pagination */}
                {meta && meta.last_page > 1 && (
                  <div className="mt-8 flex justify-center gap-2">
                    <button
                      onClick={() => fetchTickets(meta.current_page - 1)}
                      disabled={meta.current_page === 1}
                      className="rounded-xl border border-white/10 bg-white/5 px-4 py-2 text-sm font-medium hover:bg-white/10 disabled:opacity-50"
                    >
                      Sebelumnya
                    </button>
                    <span className="flex items-center px-4 text-sm text-zinc-400">
                      Hal {meta.current_page} dari {meta.last_page}
                    </span>
                    <button
                      onClick={() => fetchTickets(meta.current_page + 1)}
                      disabled={meta.current_page === meta.last_page}
                      className="rounded-xl border border-white/10 bg-white/5 px-4 py-2 text-sm font-medium hover:bg-white/10 disabled:opacity-50"
                    >
                      Selanjutnya
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

        </div>
      </div>

      {/* Modal Buat Tiket */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          <div className="w-full max-w-lg rounded-3xl bg-zinc-900 border border-zinc-800 p-6 sm:p-8 max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-extrabold text-white mb-2">Buat Tiket Dukungan</h2>
            <p className="text-zinc-400 text-sm mb-6">Lengkapi form di bawah ini untuk mengirimkan pertanyaan atau kendala.</p>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-zinc-300 mb-2">Subjek <span className="text-rose-500">*</span></label>
                <input
                  type="text"
                  required
                  value={formData.subject}
                  onChange={e => setFormData({...formData, subject: e.target.value})}
                  placeholder="Ringkasan singkat kendala Anda"
                  className="w-full rounded-xl bg-white/5 px-4 py-3 text-sm text-white ring-1 ring-white/10 focus:ring-emerald-500 outline-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-zinc-300 mb-2">Kategori <span className="text-rose-500">*</span></label>
                  <select
                    value={formData.category}
                    onChange={e => setFormData({...formData, category: e.target.value})}
                    className="w-full rounded-xl bg-white/5 px-4 py-3 text-sm text-white ring-1 ring-white/10 focus:ring-emerald-500 outline-none"
                  >
                    <option className="bg-zinc-800" value="general">Umum</option>
                    <option className="bg-zinc-800" value="technical_issue">Kendala Teknis</option>
                    <option className="bg-zinc-800" value="billing">Tagihan / Pembayaran</option>
                    <option className="bg-zinc-800" value="suggestion">Saran / Masukan</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-zinc-300 mb-2">Prioritas <span className="text-rose-500">*</span></label>
                  <select
                    value={formData.priority}
                    onChange={e => setFormData({...formData, priority: e.target.value})}
                    className="w-full rounded-xl bg-white/5 px-4 py-3 text-sm text-white ring-1 ring-white/10 focus:ring-emerald-500 outline-none"
                  >
                    <option className="bg-zinc-800" value="low">Rendah</option>
                    <option className="bg-zinc-800" value="medium">Sedang</option>
                    <option className="bg-zinc-800" value="high">Tinggi</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-zinc-300 mb-2">Pesan <span className="text-rose-500">*</span></label>
                <textarea
                  required
                  rows="4"
                  value={formData.message}
                  onChange={e => setFormData({...formData, message: e.target.value})}
                  placeholder="Ceritakan detail kendala Anda..."
                  className="w-full rounded-xl bg-white/5 px-4 py-3 text-sm text-white ring-1 ring-white/10 focus:ring-emerald-500 outline-none resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-zinc-300 mb-2">Lampiran (Opsional)</label>
                <input
                  type="file"
                  onChange={handleFileChange}
                  accept=".jpg,.jpeg,.png,.pdf"
                  className="block w-full text-sm text-zinc-400 file:mr-4 file:py-2.5 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-semibold file:bg-white/10 file:text-white hover:file:bg-white/20 transition cursor-pointer"
                />
                <p className="mt-1.5 text-xs text-zinc-500">Maks. 2MB (JPG, PNG, PDF)</p>
              </div>

              <div className="pt-4 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="rounded-xl px-5 py-2.5 text-sm font-bold text-zinc-300 hover:bg-white/5 transition"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="rounded-xl bg-emerald-500 px-6 py-2.5 text-sm font-bold text-white shadow-sm hover:bg-emerald-400 transition disabled:opacity-50"
                >
                  {submitting ? 'Memproses...' : 'Kirim Tiket'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Footer />
    </>
  );
};

export default Support;
